// Configuration and Constants
export const CONFIG = {
    SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    SUPABASE_KEY: import.meta.env.VITE_SUPABASE_KEY,
    POLLING_INTERVAL: 7000,
    AUTO_SAVE_DELAY: 2000,
    AUTH: {
        ENABLED: true,
        PASSWORD_HASH: import.meta.env.VITE_AUTH_PASSWORD_HASH || '',
        REMEMBER_KEY: 'simpal_auth_hash'
    }
};

// Initial state factory
export const getInitialState = () => ({
    flatAssignments: [], 
    disabledFlags: [], 
    exerciseNumbers: [], 
    instructorAssignments: [], // Array of instructor indices/IDs parallel to flatAssignments
    numPositions: 3, 
    rowHighlights: [],
    isEcPlcMode: false, 
    traineeNameList: [], 
    instructorNameList: [], // Instructor names/initials for history tracking
    traineeGroups: [], // Group assignments for trainees (e.g., "1", "1&2", "2")
    preferredLeaders: [], // Boolean flags for trainees preferred as leaders
    instructorGroups: [], // Group assignments for instructors
    exerciseNameList: [],
    exerciseSectorCounts: [], // Per-exercise sector counts [2, 1, 3, ...]
    exerciseSectorNames: [], // Per-exercise sector custom names [[null, null], ['North', 'South'], ...]
    sectorsPerExercise: 1, // Global default / legacy sectors per exercise setting
    usePerExerciseSectors: false, // Flag to indicate if per-exercise sector counts are in use
    startDate: new Date().toISOString().split('T')[0], 
    startDateNotes: '', 
    dayBreaks: [],
    customTexts: {},
    customRowHeaders: {}, // Object to store custom run header texts {rowIndex: "Custom Text"}
    columnNames: [],
    lockedCells: [], // Array of boolean flags for individual cell locks
    lockedRows: [], // Array of boolean flags for entire row locks
    skipTESR: {}, // Map of TESR skips: { 'traineeId|exerciseBase|sector|role': true }
    traineeUnavailableDays: {}, // Map of unavailable trainees by day: { dayIndex: { traineeId: true | [zero-based run offsets] } }
    instructorDisplayAliases: {}, // Per-day display-only instructor names: { dayIndex: { instructorIndex: "Alias" } }
});

// Global state
export let scheduleState = getInitialState();
// Initialize skipTESR map and log once
if (!scheduleState.skipTESR) {
    scheduleState.skipTESR = {};
    try { console.log('[Skip] Initialized skipTESR map'); } catch (_) {}
}
export let visualCollapseState = {};
export let currentScheduleId = null;
export let currentScheduleName = null;
export let lastUpdatedTimestamp = null;
export let isUserInteracting = false;
export let pollingInterval = null;
export let dragSrcElement = null;

// State setters
export const setScheduleState = (newState) => { scheduleState = newState; };
export const setCurrentScheduleId = (id) => { currentScheduleId = id; };
export const setCurrentScheduleName = (name) => { currentScheduleName = name; };
export const setDragSrcElement = (element) => { dragSrcElement = element; };
export const setVisualCollapseState = (state) => { visualCollapseState = state; };
export const setPollingInterval = (interval) => { pollingInterval = interval; };
export const setLastUpdatedTimestamp = (timestamp) => { lastUpdatedTimestamp = timestamp; };
export const setIsUserInteracting = (value) => { isUserInteracting = value; };

// Helper function to get sectors for a specific exercise
// Falls back to global sectorsPerExercise if per-exercise count not available
export function getSectorsForExercise(baseIndex) {
    if (scheduleState.exerciseSectorCounts && 
        scheduleState.exerciseSectorCounts.length > baseIndex && 
        scheduleState.exerciseSectorCounts[baseIndex] > 0) {
        return scheduleState.exerciseSectorCounts[baseIndex];
    }
    return scheduleState.sectorsPerExercise || 1;
}

// Helper function to get sector names for a specific exercise
// Returns array of custom names if defined, otherwise generates default letters (A, B, C, ...)
export function getSectorNamesForExercise(baseIndex) {
    const sectorCount = getSectorsForExercise(baseIndex);
    
    // Check if custom names exist for this exercise
    if (scheduleState.exerciseSectorNames && 
        scheduleState.exerciseSectorNames.length > baseIndex &&
        Array.isArray(scheduleState.exerciseSectorNames[baseIndex])) {
        const customNames = scheduleState.exerciseSectorNames[baseIndex];
        // Return custom names if they're valid (non-null/non-empty)
        const hasValidNames = customNames.some(name => name && String(name).trim());
        if (hasValidNames) {
            return customNames.map((name, i) => 
                (name && String(name).trim()) || String.fromCharCode(65 + i)
            );
        }
    }
    
    // Default: generate A, B, C, ... based on sector count
    return Array.from({length: sectorCount}, (_, i) => String.fromCharCode(65 + i));
}

function getExerciseCountFromState(state) {
    let count = Array.isArray(state.exerciseNameList) ? state.exerciseNameList.length : 0;
    if (Array.isArray(state.exerciseSectorCounts)) {
        count = Math.max(count, state.exerciseSectorCounts.length);
    }

    for (const exerciseId of state.exerciseNumbers || []) {
        if (exerciseId == null) continue;
        const baseIndex = typeof exerciseId === 'object' ? exerciseId.baseIndex : exerciseId;
        if (Number.isInteger(baseIndex) && baseIndex >= 0) {
            count = Math.max(count, baseIndex + 1);
        }
    }

    for (const key of Object.keys(state.skipTESR || {})) {
        const exerciseBase = Number.parseInt(String(key).split('|')[1], 10);
        if (Number.isInteger(exerciseBase) && exerciseBase > 0) {
            count = Math.max(count, exerciseBase);
        }
    }

    return count;
}

export function materializeGenericExerciseNames(state = scheduleState) {
    const existingNames = Array.isArray(state.exerciseNameList) ? state.exerciseNameList : [];
    if (existingNames.length > 0) return existingNames;

    const exerciseCount = getExerciseCountFromState(state);
    if (exerciseCount === 0) {
        state.exerciseNameList = [];
        return state.exerciseNameList;
    }

    state.exerciseNameList = Array.from(
        { length: exerciseCount },
        (_, index) => `Exe ${index + 1}`
    );
    return state.exerciseNameList;
}

// Backward compatibility: Populate exerciseSectorCounts from global sectorsPerExercise
// This is called when loading old schedules that only have the global setting
export function migrateExerciseSectorCounts(state = scheduleState) {
    // Older generic schedules stored no names at all. Materialize their display
    // names once so all later edits use the same representation as named schedules.
    materializeGenericExerciseNames(state);

    // Check if migration is needed
    const hasPerExerciseCounts = state.exerciseSectorCounts && 
        state.exerciseSectorCounts.length > 0 && 
        state.exerciseSectorCounts.some(count => count > 0);
    
    if (hasPerExerciseCounts) {
        // Already has per-exercise counts, no migration needed
        return;
    }
    
    // Determine number of exercises from exerciseNameList or exerciseNumbers
    let numExercises = state.exerciseNameList ? state.exerciseNameList.length : 0;
    
    if (numExercises === 0 && state.exerciseNumbers && state.exerciseNumbers.length > 0) {
        // Derive from exerciseNumbers array
        let maxBaseIndex = -1;
        for (const exeId of state.exerciseNumbers) {
            if (exeId !== null) {
                const baseIndex = typeof exeId === 'object' ? exeId.baseIndex : exeId;
                if (typeof baseIndex === 'number') {
                    maxBaseIndex = Math.max(maxBaseIndex, baseIndex);
                }
            }
        }
        numExercises = maxBaseIndex + 1;
    }
    
    // Populate exerciseSectorCounts with global sectorsPerExercise for all exercises
    if (numExercises > 0) {
        const globalSectors = state.sectorsPerExercise || 1;
        state.exerciseSectorCounts = Array(numExercises).fill(globalSectors);
        console.log(`[Migration] Populated exerciseSectorCounts for ${numExercises} exercises with ${globalSectors} sectors each`);
    }
}
